import React from "react";
import { createShortLink } from "actions/shortLink";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { fetchAppCheckout } from "actions/app";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik } from "formik";
import Padder from "components/Padder";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import ConfirmationFields from "components/ShortLinkForm/Fields/Confirmation";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import {
  prepareCreateShortLinkInitialValues,
  prepareCreateShortLink,
  getShortLinkPreviewTitle,
  validateCreateShortLinkForm
} from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import {
  ensureAppCheckout,
  ensureProductsPricesCouponsPromotionCodes,
  ensureTaxRates,
  ensureAttachments,
  ensureShippingRates
} from "utils/hooks";
import { fetchAttachments } from "actions/attachment";
import MenuNavHeader from "components/MenuNavHeader";
import { useCtxReferences, useProduct } from "utils/selectors";
import { THEMES } from "utils/constants/ui";
import isEmpty from "lodash/isEmpty";
import compact from "lodash/compact";
import InfoList from "components/InfoList";
import CheckoutDetails from "components/SaleForm/Fields/CheckoutDetails";
import { STATE_KEYS } from "utils/constants/state";
import { SHORT_LINK_NODE_KEY } from "utils/constants/shortLink";

const ProductNewPaymentLinkForm = ({ uid, actions, goBack }) => {
  const product = useProduct(uid);
  const {
    products,
    prices,
    coupons,
    promotionCodes
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });
  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });

  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });
  const { attachments } = ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  const productPrices = prices.filter((price) => price.product === uid);

  const {
    application,
    merchantAccount,
    resourceID,
    resourceUUID,
    resource
  } = useCtxReferences();

  const submitForm = (formValues, formikActions) => {
    const data = prepareCreateShortLink({
      values: formValues,
      products,
      prices,
      merchantAccount,
      coupons,
      promotionCodes,
      attachments
    });

    actions.createShortLink(
      {
        resourceID,
        resourceUUID,
        resource,
        data
      },
      (err, _) => {
        formikActions.setSubmitting(false);
        if (err) {
          formikActions.setErrors(err);
        } else {
          formikActions.resetForm({ values: formValues });
          goBack();
        }
      }
    );
  };

  const initialValues = prepareCreateShortLinkInitialValues({
    application,
    merchantAccount,
    product,
    productPrices
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCreateShortLinkForm({
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })}
    >
      {({ values, submitForm, isSubmitting, errors }) => {
        const checkoutPreview = shortLinkCheckoutPreview({
          shortLink: values,
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        });

        const previewSubtotal = getShortLinkPreviewTitle({
          shortLink: values,
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        });

        const messagesList = errors &&
          errors.messages &&
          errors.messages.length > 0 && (
            <div className="pt3 w-100 dib">
              <InfoList
                header="Error"
                items={errors.messages}
                theme={THEMES.ERROR}
              />
            </div>
          );

        return (
          <div className="w-100">
            <MenuNavHeader
              label={previewSubtotal}
              customClasses={{
                container:
                  "flex flex-column justify-between items-end sticky-l bb hairline-1 bg-white bh--inset top-0 z-5",
                content: "pb3 ph3 pl1 pt3 w-100 flex flex-row",
                label: "fw6 f3 word-break"
              }}
              action={{
                onClick: submitForm,
                copy: `Create`,
                disabled: !isEmpty(errors),
                loading: isSubmitting
              }}
            />
            <div className="pa3">
              {compact([
                messagesList
                  ? {
                      key: SHORT_LINK_NODE_KEY.MESSAGES,
                      node: messagesList
                    }
                  : null,
                {
                  key: SHORT_LINK_NODE_KEY.PRODUCTS,
                  node: <ProductsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.PAYMENT,
                  node: <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.TAX,
                  node: <TaxRatesFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.COUPONS,
                  node: <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.RECEIVER,
                  node: <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.SHIPPING,
                  node: <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
                },
                {
                  key: SHORT_LINK_NODE_KEY.REDIRECT,
                  node: <RedirectsFields />
                },
                {
                  key: SHORT_LINK_NODE_KEY.CONFIRMATION,
                  node: (
                    <ConfirmationFields
                      name={STATE_KEYS.SHORT_LINK.CONFIRMATION_DATA}
                    />
                  )
                },
                {
                  key: SHORT_LINK_NODE_KEY.CHECKOUT_PREVIEW,
                  node: (
                    <CheckoutDetails
                      header="Checkout preview"
                      collapseImages={checkoutPreview.collapseImages}
                      {...checkoutPreview.checkout}
                    />
                  )
                }
              ]).map(({ node, key }, nodeIx) => (
                <div className={nodeIx ? "pt3 pt4-ns" : ""} key={key}>
                  {node}
                </div>
              ))}
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink,
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ProductNewPaymentLinkForm);
